// Homepage "tiers" defaults (EN + FR): the agent tiers grid (src/Sections/Services).
// Each tier's photo is built into that component (by position) until one is uploaded; the
// service-page links are stored once (English side) and localized for French.
const TIERS_DEFAULTS = {
  "en": {
    "eyebrow": "What We Do",
    "title": "Security Guards & Specialists in Quebec and Ontario",
    "subtitle": "Choose the right level of protection—from regular security officers to experienced supervisors, intervention specialists, and close protection professionals.",
    "items": [
      {
        "title": "Standard Security Officer",
        "desc": "Our Standard Security Agents are licensed professionals trained to provide reliable on-site security. They handle access control, patrol duties, incident reporting, and general surveillance. Ideal for residential buildings, construction sites, retail locations, and events requiring a professional security presence.",
        "points": [
          { "text": "Licensed & certified" },
          { "text": "Access control & patrol" },
          { "text": "Incident reporting" },
          { "text": "24/7 availability" }
        ],
        "linkLabel": "Event Security",
        "buttonLabel": "View Event Security services →",
        "link": "/services/event-security"
      },
      {
        "title": "Senior Security Officer",
        "desc": "Experienced Security Agents bring years of field expertise and advanced training to high-demand environments. They excel in conflict de-escalation, emergency response coordination, and complex site management. Best suited for corporate offices, high-traffic venues, and sensitive locations.",
        "points": [
          { "text": "2+ years field experience" },
          { "text": "Conflict de-escalation" },
          { "text": "Emergency coordination" },
          { "text": "Advanced site management" }
        ],
        "linkLabel": "Residential Security",
        "buttonLabel": "View Residential Security services →",
        "link": "/services/residential-security"
      },
      {
        "title": "Security Supervisor",
        "desc": "Our Elite and Supervisor-level agents are seasoned leaders who oversee security operations and teams. They develop security plans, conduct threat assessments, manage multi-agent deployments, and serve as the primary liaison between clients and our operations center.",
        "points": [
          { "text": "Team leadership & oversight" },
          { "text": "Threat assessment" },
          { "text": "Security plan development" },
          { "text": "Client liaison" }
        ],
        "linkLabel": "Commercial Properties",
        "buttonLabel": "View Commercial Properties services →",
        "link": "/services/commercial-properties"
      },
      {
        "title": "Intervention Officer",
        "desc": "Intervention Specialists are rapid-response professionals trained to handle high-risk situations including trespasser removal, alarm response, and emergency lockdowns. They operate with precision and composure under pressure, ensuring swift resolution of security incidents.",
        "points": [
          { "text": "Handcuff & arrest training" },
          { "text": "Trespasser removal" },
          { "text": "Emergency lockdowns" },
          { "text": "High-pressure composure" }
        ],
        "linkLabel": "Crisis Intervention",
        "buttonLabel": "View Crisis Intervention services →",
        "link": "/services/crisis-intervention-security"
      },
      {
        "title": "Close Protection Specialist",
        "desc": "Our Tactical and Close Protection Specialists provide executive-level protection for VIPs, high-net-worth individuals, and sensitive operations. They are trained in advanced threat detection, secure transportation protocols, and discreet personal security.",
        "points": [
          { "text": "Executive protection" },
          { "text": "Threat detection" },
          { "text": "Secure transport" },
          { "text": "Discreet personal security" }
        ],
        "linkLabel": "Fire Watch",
        "buttonLabel": "View Fire Watch services →",
        "link": "/services/fire-watch-security"
      },
      {
        "title": "Concierge / Hospitality Specialist",
        "desc": "Concierge and Hospitality Specialists blend security expertise with exceptional guest services. They manage front-desk operations, visitor management, and VIP reception while maintaining a secure environment. Perfect for hotels, luxury residences, and corporate lobbies.",
        "points": [
          { "text": "Guest-centric service" },
          { "text": "Visitor management" },
          { "text": "VIP reception" },
          { "text": "Front-desk operations" }
        ],
        "linkLabel": "Hospitality Security",
        "buttonLabel": "View Hospitality Security services →",
        "link": "/services/hospitality-security"
      }
    ],
    "exploreLabel": "Explore",
    "closeLabel": "Close",
    "aboutLabel": "About This Role",
    "linksLabel": "Explore our services:",
    "linksAriaLabel": "Related services"
  },
  "fr": {
    "eyebrow": "Ce que nous faisons",
    "title": "Agents de sécurité et spécialistes au Québec et en Ontario",
    "subtitle": "Choisissez le niveau de protection adapté à vos besoins : agents de sécurité réguliers, agents seniors, superviseurs, agents d’intervention et spécialistes en protection rapprochée.",
    "items": [
      {
        "title": "Agent de sécurité régulier",
        "desc": "Nos agents de sécurité standard sont des professionnels agréés formés pour assurer une sécurité fiable sur site. Ils gèrent le contrôle d'accès, les rondes de patrouille, les rapports d'incidents et la surveillance générale. Idéal pour les immeubles résidentiels, les chantiers de construction, les commerces et les événements nécessitant une présence sécuritaire professionnelle.",
        "points": [
          { "text": "Agréé et certifié" },
          { "text": "Contrôle d'accès et patrouille" },
          { "text": "Rapports d'incidents" },
          { "text": "Disponibilité 24/7" }
        ],
        "linkLabel": "Sécurité événementielle",
        "buttonLabel": "Voir Sécurité événementielle →"
      },
      {
        "title": "Agent de sécurité senior",
        "desc": "Les agents de sécurité expérimentés apportent des années d'expertise terrain et une formation avancée aux environnements à forte demande. Ils excellent dans la désescalade de conflits, la coordination des interventions d'urgence et la gestion de sites complexes. Idéal pour les bureaux d'entreprise, les lieux à fort achalandage et les sites sensibles.",
        "points": [
          { "text": "2+ ans d'expérience terrain" },
          { "text": "Désescalade de conflits" },
          { "text": "Coordination d'urgence" },
          { "text": "Gestion de site avancée" }
        ],
        "linkLabel": "Sécurité résidentielle",
        "buttonLabel": "Voir Sécurité résidentielle →"
      },
      {
        "title": "Superviseur de sécurité",
        "desc": "Nos agents de niveau élite et superviseur sont des leaders chevronnés qui supervisent les opérations et les équipes de sécurité. Ils élaborent des plans de sécurité, effectuent des évaluations de menaces, gèrent les déploiements multi-agents et servent d'intermédiaires principaux entre les clients et notre centre d'opérations.",
        "points": [
          { "text": "Leadership et supervision d'équipe" },
          { "text": "Évaluation des menaces" },
          { "text": "Élaboration de plans de sécurité" },
          { "text": "Liaison avec le client" }
        ],
        "linkLabel": "Propriétés commerciales",
        "buttonLabel": "Voir Propriétés commerciales →"
      },
      {
        "title": "Agent d’intervention",
        "desc": "Les spécialistes en intervention sont des professionnels d'intervention rapide formés pour gérer les situations à haut risque, incluant l'expulsion d'intrus, la réponse aux alarmes et les confinements d'urgence. Ils opèrent avec précision et sang-froid sous pression, assurant une résolution rapide des incidents de sécurité.",
        "points": [
          { "text": "Formation menotage et arrestation" },
          { "text": "Expulsion d'intrus" },
          { "text": "Confinements d'urgence" },
          { "text": "Sang-froid sous pression" }
        ],
        "linkLabel": "Intervention de crise",
        "buttonLabel": "Voir Intervention de crise →"
      },
      {
        "title": "Spécialiste en protection rapprochée",
        "desc": "Nos spécialistes tactiques et de protection rapprochée offrent une protection de niveau exécutif pour les VIP, les personnes à valeur nette élevée et les opérations sensibles. Ils sont formés en détection avancée des menaces, protocoles de transport sécurisé et sécurité personnelle discrète.",
        "points": [
          { "text": "Protection exécutive" },
          { "text": "Détection des menaces" },
          { "text": "Transport sécurisé" },
          { "text": "Sécurité personnelle discrète" }
        ],
        "linkLabel": "Surveillance incendie",
        "buttonLabel": "Voir Surveillance incendie →"
      },
      {
        "title": "Spécialiste conciergerie / hospitalité",
        "desc": "Les spécialistes en conciergerie et hospitalité allient expertise en sécurité et service client exceptionnel. Ils gèrent les opérations d'accueil, la gestion des visiteurs et la réception VIP tout en maintenant un environnement sécurisé. Parfait pour les hôtels, les résidences de luxe et les halls d'entreprise.",
        "points": [
          { "text": "Service axé sur le client" },
          { "text": "Gestion des visiteurs" },
          { "text": "Réception VIP" },
          { "text": "Opérations d'accueil" }
        ],
        "linkLabel": "Sécurité hôtelière",
        "buttonLabel": "Voir Sécurité hôtelière →"
      }
    ],
    "exploreLabel": "Découvrir",
    "closeLabel": "Fermer",
    "aboutLabel": "À propos de ce rôle",
    "linksLabel": "Découvrez nos services :",
    "linksAriaLabel": "Services connexes"
  }
};

export default TIERS_DEFAULTS;
